/**
 * Where a venue's photos sit in the crop dialog.
 *
 * The dialog is a singleton keyed by a slot id, so two fields sharing an id
 * would hand each other's photo back. The named slots (behind the name, beside
 * the description, a block's own) are one each; the gallery's are keyed by
 * POSITION, which is why a gallery photo that moves has to have its crop
 * forgotten.
 */
export type PhotoSlot =
  | 'hero'
  | 'about'
  | `gallery-${number}`
  | `extra-${string}`

export const photoSlotId = (slot: PhotoSlot) => `website-photo-${slot}`

/**
 * The gallery without one of its photos. The gap closes, because the gallery
 * renders as a strip of pictures and the schema refuses a picture with no
 * reference, so a hole in the middle would make the whole page unpublishable.
 */
export const galleryWithout = (photos: string[], offset: number): string[] =>
  photos.filter((_, index) => index !== offset)

/**
 * The crop slots to forget when a gallery photo is removed.
 *
 * Closing the gap shifts every photo after it one tile to the left, and the
 * slots are keyed by position, so the crops left behind would be drawn against
 * the wrong photos and the empty tile at the end would show the one that used
 * to be there. Dropping these makes each tile fall back to the photo it now
 * holds.
 */
export const gallerySlotIdsFrom = (photos: string[], offset: number): string[] =>
  Array.from({ length: photos.length - offset + 1 }, (_, step) =>
    photoSlotId(`gallery-${offset + step}`)
  )
